import { useSuspenseQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { workerWeekOptions } from "@/api/me/query-options";
import { DayRow } from "@/components/vroom/day-row";
import { IndexPageHeader, IndexPageHeaderTitle } from "@/components/vroom/index-page-header";
import { SubmitWeekDialog } from "@/components/work/submit-week-dialog";
import { isoWeekFromDate, isoWeekRange } from "@/lib/temporal";
import type { MeWeekResponse } from "@/api/generated/types.gen";

// ── week string helpers ────────────────────────────────────────────────────

function parseWeek(weekISO: string): [number, number] {
  const m = weekISO.match(/^(\d{4})-W(\d{2})$/);
  if (!m) throw new Error(`Invalid week ISO: ${weekISO}`);
  return [Number(m[1]), Number(m[2])];
}

function shiftWeek(weekISO: string, delta: number): string {
  const [y, w] = parseWeek(weekISO);
  const { fromDate } = isoWeekRange(y, w);
  const next = new Date(`${fromDate}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + delta * 7);
  return isoWeekFromDate(next);
}

function minutesBetween(startMs: number, endMs: number): number {
  return Math.round((endMs - startMs) / 60000);
}

// ── route ─────────────────────────────────────────────────────────────────

interface WorkContext {
  user: { id: string; name: string; email: string; role?: string };
  queryClient: QueryClient;
}

export const Route = createFileRoute("/_authed/work/")({
  validateSearch: (s: Record<string, unknown>): { week?: string; submit?: true } => ({
    week: typeof s.week === "string" ? s.week : undefined,
    submit: s.submit === true || s.submit === "true" ? true : undefined,
  }),
  loaderDeps: ({ search }) => ({ week: search.week ?? isoWeekFromDate(new Date()) }),
  loader: ({ deps, context }) => {
    const ctx = context as WorkContext;
    // Prefetch adjacent weeks so prev/next clicks are instant.
    // Fire-and-forget — don't block render.
    void ctx.queryClient.prefetchQuery(workerWeekOptions(ctx.user.id, shiftWeek(deps.week, -1)));
    void ctx.queryClient.prefetchQuery(workerWeekOptions(ctx.user.id, shiftWeek(deps.week, 1)));
    return ctx.queryClient.ensureQueryData(workerWeekOptions(ctx.user.id, deps.week));
  },
  component: WorkHome,
});

function WorkHome() {
  const { t } = useTranslation();
  const { user } = Route.useRouteContext() as { user: { id: string } };
  const { week: weekParam, submit: submitParam } = Route.useSearch();
  const week = weekParam ?? isoWeekFromDate(new Date());
  const navigate = useNavigate();
  const submitOpen = submitParam === true;

  function setSubmitOpen(open: boolean) {
    void navigate({
      to: "/work",
      search: open ? { week, submit: true } : { week },
      replace: true,
      viewTransition: false,
      resetScroll: false,
    });
  }

  const { data } = useSuspenseQuery(workerWeekOptions(user.id, week));
  const weekData = data as MeWeekResponse;

  const [year, weekNum] = parseWeek(week);
  const { fromDate, toDate } = isoWeekRange(year, weekNum);

  // Build date list for this week (Mon–Sun)
  const days: Date[] = [];
  const start = new Date(`${fromDate}T00:00:00Z`);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    days.push(d);
  }

  function goToWeek(w: string) {
    void navigate({ to: "/work", search: { week: w } });
  }

  const prevLabel = shiftWeek(week, -1);
  const nextLabel = shiftWeek(week, 1);

  const weekLabel = t("work.home.weekLabel", {
    week: `${year}-W${String(weekNum).padStart(2, "0")}`,
  });

  return (
    <>
      <IndexPageHeader>
        <IndexPageHeaderTitle
          title={t("work.home.title")}
          kicker={weekLabel}
          onPrev={() => goToWeek(prevLabel)}
          onNext={() => goToWeek(nextLabel)}
        />
      </IndexPageHeader>
      <div className="space-y-3 px-4 py-4 md:px-6 md:py-6">
        <div className="space-y-2">
          {days.map((day) => {
            const dateKey = day.toISOString().slice(0, 10);
            const dayEntries = weekData.entries.filter((e) => e.date === dateKey);
            const hoursMinutes = dayEntries.reduce(
              (acc, e) => acc + minutesBetween(e.startAt, e.endAt),
              0,
            );
            const dayBreak = weekData.dayBreaks.find((b) => b.date === dateKey);
            const breakMinutes = dayBreak ? dayBreak.breakDurationMinutes : 0;
            const hasLodging = weekData.lodgings.some((l) => {
              const nightEnd = new Date(l.nightEndedOn);
              return nightEnd.toISOString().slice(0, 10) === dateKey;
            });
            const rolledStatus = weekData.rolledUpStatusPerDay[dateKey] ?? null;
            return (
              <DayRow
                key={dateKey}
                date={day}
                hoursMinutes={hoursMinutes}
                expectedMinutes={480}
                hasLodging={hasLodging}
                breakMinutes={breakMinutes}
                rolledStatus={rolledStatus}
                onClick={() => void navigate({ to: "/work/day/$date", params: { date: dateKey } })}
              />
            );
          })}
        </div>

        <div className="pt-2">
          <button
            type="button"
            className="w-full rounded-4 bg-p-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-p-700 active:bg-p-800"
            onClick={() => setSubmitOpen(true)}
          >
            {t("work.home.submitWeek")}
          </button>
        </div>

        <p className="text-[10px] text-ink-400">
          {fromDate} – {toDate}
        </p>
      </div>
      <SubmitWeekDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        userId={user.id}
        week={week}
        onSubmitted={() => setSubmitOpen(false)}
      />
    </>
  );
}
